'use client';

import { type CSSProperties } from 'react';
import * as RCheckbox from '@radix-ui/react-checkbox';
import { cn } from '../../lib/cn';
import { SIcon } from '../SIcon';

export type SCheckboxValue = boolean | unknown[] | null;

export interface SCheckboxProps {
  /** 선택 상태. boolean(단일) / 배열(그룹) / null(indeterminate) */
  value?: SCheckboxValue;
  /** 배열(그룹) 모드에서 이 체크박스의 값 */
  val?: unknown;
  /** 값 변경 (sdUpdate) */
  onValueChange?: (value: boolean | unknown[]) => void;
  /** 라벨 */
  label?: string;
  /** 비활성 */
  disabled?: boolean;
  /** 어두운 배경용 흰색 스타일 */
  inverse?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * SCheckbox — sd-checkbox 포팅 (Radix SCheckbox).
 * boolean(단일)·배열(그룹, val 필요)·null(indeterminate) 지원, inverse 스타일.
 */
export function SCheckbox({
  value = false,
  val,
  onValueChange,
  label = '',
  disabled = false,
  inverse = false,
  className,
  style,
}: SCheckboxProps) {
  const state: boolean | 'indeterminate' =
    value === null
      ? 'indeterminate'
      : typeof value === 'boolean'
        ? value
        : Array.isArray(value)
          ? value.includes(val)
          : false;

  const isChecked = state === true || state === 'indeterminate';

  const handleChange = () => {
    if (disabled || !onValueChange) return;
    if (Array.isArray(value)) {
      const set = new Set(value);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      onValueChange(Array.from(set));
    } else if (typeof value === 'boolean') {
      onValueChange(!value);
    } else {
      onValueChange(false); // null → false (원본 동작)
    }
  };

  // 색상은 전부 토큰(--cmp-checkbox-*) 참조 → token.json 변경이 전역 반영
  const boxBg = disabled
    ? 'var(--cmp-checkbox-bg-disabled)'
    : inverse
      ? 'transparent'
      : isChecked
        ? 'var(--cmp-checkbox-checked-bg-default)'
        : 'var(--cmp-checkbox-unchecked-bg-default)';
  const boxBorder = disabled
    ? 'var(--cmp-checkbox-border-disabled)'
    : inverse
      ? 'var(--cmp-checkbox-checked-border-inverse)'
      : isChecked
        ? 'var(--cmp-checkbox-checked-bg-default)'
        : 'var(--cmp-checkbox-unchecked-border-default)';
  const iconColor = disabled
    ? 'var(--cmp-checkbox-checked-icon-disabled)'
    : 'var(--cmp-checkbox-checked-icon-default)';
  const labelColor = disabled
    ? 'var(--cmp-checkbox-label-disabled)'
    : inverse
      ? 'var(--cmp-checkbox-label-inverse)'
      : 'var(--cmp-checkbox-label-default)';

  return (
    <label
      className={cn(
        'inline-flex items-center gap-[var(--cmp-checkbox-gap)] align-middle',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
      style={style}
    >
      <RCheckbox.Root
        checked={state}
        disabled={disabled}
        onCheckedChange={handleChange}
        className={cn(
          'flex h-[var(--cmp-checkbox-size)] w-[var(--cmp-checkbox-size)] items-center justify-center rounded-[var(--cmp-checkbox-radius)] border-[length:var(--cmp-checkbox-borderWidth)] border-solid bg-[var(--cb-bg)] border-[color:var(--cb-border)] outline-none',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          !disabled &&
            !isChecked &&
            !inverse &&
            'hover:border-[color:var(--cmp-checkbox-unchecked-border-hover)] hover:bg-[var(--cmp-checkbox-unchecked-bg-hover)]',
        )}
        style={{ '--cb-bg': boxBg, '--cb-border': boxBorder } as CSSProperties}
      >
        <RCheckbox.Indicator className="flex items-center justify-center leading-none">
          <SIcon
            name={state === 'indeterminate' ? 'checkboxIndeterminate' : 'checkboxCheck'}
            size={12}
            color={iconColor}
          />
        </RCheckbox.Indicator>
      </RCheckbox.Root>
      {label && (
        // 고스트(500) 텍스트로 너비를 고정 → 두께 400↔500 전환 시 레이아웃이 밀리지 않음
        <span className="grid text-nowrap text-[12px] leading-[20px]" style={{ color: labelColor }}>
          <span aria-hidden className="invisible font-medium" style={{ gridArea: '1 / 1' }}>
            {label}
          </span>
          <span style={{ gridArea: '1 / 1', fontWeight: isChecked ? 500 : 400 }}>{label}</span>
        </span>
      )}
    </label>
  );
}
